import type { TFileWithMeta } from '@components/Files/UploadProvider/types/TFileWithMeta';
import { useAPI } from '@providers/useAPI';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { CommonBullJobResponseDto } from 'app/API';
import { createFetchLegacyAnalysisJobQueryFn } from 'app/queries/media/createFetchLegacyAnalysisJobQueryFn';
import {
  createFetchMediaAnalysisProgressQueryFn,
  type TMediaAnalysisPoll,
  type TMediaAnalysisProgress,
} from 'app/queries/media/createFetchMediaAnalysisProgressQueryFn';
import { mediaKeys } from 'app/queries/media/mediaKeys';
import { useCallback, useEffect, useMemo } from 'react';
import {
  collectPendingMediaAnalyses,
  type TPendingMediaAnalysis,
} from '@helpers/mediaAnalysis/collectPendingMediaAnalyses';
import { collectUnstartedMediaAnalysisFileIds } from '@helpers/mediaAnalysis/collectUnstartedMediaAnalysisFileIds';
import { getMediaAnalysisExtra } from '@helpers/mediaAnalysis/getMediaAnalysisExtra';
import { getMediaAnalysisRecord } from '@helpers/mediaAnalysis/getMediaAnalysisRecord';
import { groupMediaIdsByJob } from '@helpers/mediaAnalysis/groupMediaIdsByJob';
import { needsLegacyAnalysisLookup } from '@helpers/mediaAnalysis/needsLegacyAnalysisLookup';
import {
  MEDIA_ANALYSIS_TIMEOUT_MS,
  resolveMediaAnalysisOutcome,
} from '@helpers/mediaAnalysis/resolveMediaAnalysisOutcome';

export const MEDIA_ANALYSIS_POLL_INTERVAL_MS = 5000;
export const MEDIA_ANALYSIS_MAX_CONSECUTIVE_FAILURES = 5;

type TLegacyAnalysisJob = Partial<CommonBullJobResponseDto> | null;

type TParams = {
  files: TFileWithMeta[];
  setExtra: (id: string, extra: Record<string, unknown>) => void;
  jobId?: string;
};

export const useMediaAnalysisPolling = ({ files, setExtra, jobId: fallbackJobId = '' }: TParams) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();

  const unstartedFileIds = collectUnstartedMediaAnalysisFileIds(files).join(',');
  useEffect(() => {
    if (!unstartedFileIds) {
      return;
    }
    const startedAt = Date.now();
    unstartedFileIds.split(',').forEach((fileId) => setExtra(fileId, { analysisStartedAt: startedAt }));
  }, [unstartedFileIds, setExtra]);

  const pendingAnalysesSignature = JSON.stringify(collectPendingMediaAnalyses(files, fallbackJobId));
  const pendingAnalyses = useMemo(
    () => JSON.parse(pendingAnalysesSignature) as TPendingMediaAnalysis[],
    [pendingAnalysesSignature],
  );

  const mediaIdsByJob = useMemo(() => groupMediaIdsByJob(pendingAnalyses), [pendingAnalyses]);

  const getAnalysisState = useCallback(
    (jobId: string) => queryClient.getQueryState<TMediaAnalysisPoll>(mediaKeys.analysisProgress(jobId)),
    [queryClient],
  );

  const getAnalysisProgress = useCallback(
    (jobId: string, mediaId: string): TMediaAnalysisProgress | undefined => {
      const record = getMediaAnalysisRecord(getAnalysisState(jobId)?.data?.recordsByMediaId, mediaId);
      if (!record) {
        return undefined;
      }
      const legacyJob = queryClient.getQueryData<TLegacyAnalysisJob>(mediaKeys.legacyAnalysisJob(mediaId));
      return legacyJob ? { ...record, legacyJob } : record;
    },
    [getAnalysisState, queryClient],
  );

  const analysisQueries = useQueries({
    queries: Object.entries(mediaIdsByJob).map(([jobId, mediaIds]) => ({
      queryKey: mediaKeys.analysisProgress(jobId),
      queryFn: createFetchMediaAnalysisProgressQueryFn(call),
      refetchInterval: (query: { state: { data?: TMediaAnalysisPoll; status: string } }) =>
        query.state.status !== 'error' &&
        (query.state.data?.consecutiveFailures ?? 0) < MEDIA_ANALYSIS_MAX_CONSECUTIVE_FAILURES &&
        mediaIds.some(
          (mediaId) =>
            resolveMediaAnalysisOutcome(query.state.data?.recordsByMediaId?.[mediaId], 0).status === 'pending',
        )
          ? MEDIA_ANALYSIS_POLL_INTERVAL_MS
          : false,
      refetchIntervalInBackground: true,
      retry: false,
      gcTime: 0,
    })),
  });

  const recordsByJob = Object.fromEntries(
    Object.keys(mediaIdsByJob).map((jobId, index) => [jobId, analysisQueries[index]?.data?.recordsByMediaId]),
  );
  const legacyMediaIds = pendingAnalyses
    .filter(({ jobId, mediaId }) => needsLegacyAnalysisLookup(getMediaAnalysisRecord(recordsByJob[jobId], mediaId)))
    .map(({ mediaId }) => mediaId);

  const legacyQueries = useQueries({
    queries: legacyMediaIds.map((mediaId) => ({
      queryKey: mediaKeys.legacyAnalysisJob(mediaId),
      queryFn: createFetchLegacyAnalysisJobQueryFn(call),
      refetchInterval: (query: { state: { data?: TLegacyAnalysisJob } }) =>
        query.state.data?.returnvalue || query.state.data?.failedReason ? false : MEDIA_ANALYSIS_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      retry: false,
      gcTime: 0,
    })),
  });

  const analysisUpdates = [...analysisQueries, ...legacyQueries]
    .map((query) => `${query.dataUpdatedAt}:${query.errorUpdatedAt}`)
    .join(',');

  useEffect(() => {
    if (!analysisUpdates) {
      return;
    }
    pendingAnalyses.forEach(({ fileId, jobId, mediaId, startedAt }) => {
      const state = getAnalysisState(jobId);
      const outcome =
        state?.status === 'error' || (state?.data?.consecutiveFailures ?? 0) >= MEDIA_ANALYSIS_MAX_CONSECUTIVE_FAILURES
          ? ({ status: 'unreachable' } as const)
          : resolveMediaAnalysisOutcome(getAnalysisProgress(jobId, mediaId), Date.now() - startedAt);
      const extra = getMediaAnalysisExtra(outcome);
      if (extra) {
        setExtra(fileId, extra);
      }
    });
  }, [analysisUpdates, pendingAnalyses, getAnalysisState, getAnalysisProgress, setExtra]);

  useEffect(() => {
    const deadlineTimers = pendingAnalyses.map(({ fileId, jobId, mediaId, startedAt }) =>
      setTimeout(
        () => {
          const progress = getAnalysisProgress(jobId, mediaId);
          if (resolveMediaAnalysisOutcome(progress, MEDIA_ANALYSIS_TIMEOUT_MS).status === 'timedOut') {
            setExtra(fileId, getMediaAnalysisExtra({ status: 'timedOut' }) ?? {});
          }
        },
        Math.max(0, startedAt + MEDIA_ANALYSIS_TIMEOUT_MS - Date.now()),
      ),
    );
    return () => deadlineTimers.forEach(clearTimeout);
  }, [pendingAnalyses, getAnalysisProgress, setExtra]);
};
